import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardTitle,
  DataTable,
  DataTableColumnHeader,
  DateUI,
  Input,
  Label,
  PrimaryFullScreenSpinner,
  Txt,
  toast,
} from "@repo/ui";
import { decimalSchema, isCurrency, neverFunc } from "@repo/utils";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { type ChangeEvent, Suspense, useMemo, useState } from "react";
import { z } from "zod";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import {
  ledgerEntryDirectionValues,
  transactionTypeValues,
} from "~/gql/client/enumValues";
import type {
  CreateLedgerTransactionInput,
  TransactionBatchImportAssetFragment,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

const csvDecimalSchema = z.preprocess((value) => {
  if (typeof value !== "string") {
    return value;
  }

  const normalizedValue = value.trim().replaceAll(",", "");
  if (
    normalizedValue.startsWith(`"`) &&
    normalizedValue.endsWith(`"`) &&
    normalizedValue.length >= 2
  ) {
    return normalizedValue.slice(1, -1);
  }

  return normalizedValue;
}, decimalSchema);

const transactionCsvRowSchema = z.object({
  effectiveAt: z
    .string()
    .min(1, "Effective date is required")
    .transform(parseCsvDate),
  organisation: z.string().min(1, "Organisation is required"),
  businessEntity: z.string().min(1, "Business Entity is required"),
  asset: z.string().min(1, "Asset is required"),
  direction: z.enum(["CREDIT", "DEBIT"]),
  description: z.string(),
  reference: z.string(),
  unitId: z.string().min(2, "Unit is required"),
  normalAmount: csvDecimalSchema,
  normalUnitPrice: csvDecimalSchema,
  unitAmount: csvDecimalSchema,
  purpose: z.enum(transactionTypeValues),
});

const transactionCsvHeaders = [
  "effectiveAt",
  "organisation",
  "businessEntity",
  "asset",
  "direction",
  "description",
  "reference",
  "unitId",
  "normalAmount",
  "normalUnitPrice",
  "unitAmount",
  "purpose",
] as const;

type Row = {
  rowNumber: number;
  input: z.output<typeof transactionCsvRowSchema>;
};

type TransactionBatchAsset = TransactionBatchImportAssetFragment & {
  businessEntity: {
    id: string;
    name: string;
    organisation: {
      id: string;
      name: string;
    } | null;
  } | null;
};

type ResolvedBatchTransaction = {
  rowNumber: number;
  input: CreateLedgerTransactionInput;
  organisationName: string;
  businessEntityName: string;
  assetName: string;
  assetId: string;
  businessEntityId: string;
  organisationId: string;
};

export const Route = createFileRoute("/__auth/transactions_/batch-import")({
  head: () => opsHead("Batch import transactions"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function View() {
  const [parsedTransactions, setParsedTransactions] = useState<Row[] | null>(
    null,
  );
  const [parseError, setParseError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);

  const [createTransactions, { loading: createLoading }] = useMutation(
    graphql(`
      mutation BatchImportCreateTransactions($input: CreateLedgerTransactionsInput!) {
        createLedgerTransactions(input: $input) {
          transactions {
            id
          }
        }
      }
    `),
  );

  const { data: assetsData } = useSuspenseQuery(
    graphql(`
      query TransactionBatchImportAssets {
        organisations {
          id
          name
          businessEntities {
            id
            name
            assets {
              ...TransactionBatchImportAsset
            }
          }
        }
      }

      fragment TransactionBatchImportAsset on Asset {
        id
        name
        ... on FixedIncomeFundAsset {
          product {
            id
            ... on FundProduct {
              unit {
                id
              }
            }
          }
        }
      }
    `),
  );

  const handleBatchImportFile = async (file: File) => {
    try {
      setImporting(true);
      const batchTransactions = parseBatchImportCsv({
        csvFileContents: await file.text(),
      });
      setParsedTransactions(batchTransactions);
      setFileName(file.name);
      setParseError(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setParsedTransactions(null);
      setFileName(null);
      setParseError(message);
      toast({
        kind: "error",
        autoHide: false,
        title: "Error parsing batch import",
        description: message,
      });
    } finally {
      setImporting(false);
    }
  };

  const { error: resolutionError, resolvedTransactions } = useMemo(() => {
    if (!parsedTransactions) {
      return {
        error: null as string | null,
        resolvedTransactions: [],
      };
    }

    try {
      return {
        error: null as string | null,
        resolvedTransactions: resolveBatchTransactions({
          rows: parsedTransactions,
          assets:
            assetsData.organisations?.flatMap(
              (organisation) =>
                organisation.businessEntities?.flatMap(
                  (businessEntity) =>
                    businessEntity.assets?.map((asset) => ({
                      ...asset,
                      businessEntity: {
                        id: businessEntity.id,
                        name: businessEntity.name,
                        organisation: {
                          id: organisation.id,
                          name: organisation.name,
                        },
                      },
                    })) ?? [],
                ) ?? [],
            ) ?? [],
        }),
      };
    } catch (error) {
      return {
        error: error instanceof Error ? error.message : String(error),
        resolvedTransactions: [],
      };
    }
  }, [assetsData.organisations, parsedTransactions]);

  const createLedgerTransactionInputs = useMemo(() => {
    return resolvedTransactions.map((transaction) => transaction.input);
  }, [resolvedTransactions]);

  const navigate = useNavigate();

  const columns: ColumnDef<(typeof resolvedTransactions)[number]>[] = [
    {
      accessorKey: "rowNumber",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Row" />
      ),
      cell: ({ row }) => row.original.rowNumber,
    },
    {
      id: "effectiveAt",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Effective at" />
      ),
      cell: ({ row }) => (
        <DateUI
          date={new Date(row.original.input.effectiveAt)}
          variant="DATE"
        />
      ),
    },
    {
      accessorKey: "transactionType",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Transaction type" />
      ),
      cell: ({ row }) => row.original.input.transactionType,
    },
    {
      accessorKey: "organisationName",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Organisation" />
      ),
      cell: ({ row }) => row.original.organisationName,
    },
    {
      accessorKey: "businessEntityName",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Business Entity" />
      ),
      cell: ({ row }) => row.original.businessEntityName,
    },
    {
      accessorKey: "assetName",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Asset" />
      ),
      cell: ({ row }) => row.original.assetName,
    },
    {
      accessorKey: "direction",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Direction" />
      ),
      cell: ({ row }) => row.original.input.direction,
    },
    {
      accessorKey: "unitId",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Unit ID" />
      ),
      cell: ({ row }) => row.original.input.unitId,
    },
    {
      accessorKey: "normalAmount",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Normal amount" />
      ),
      cell: ({ row }) => row.original.input.normalAmount.toString(),
    },
    {
      id: "unitDetails",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Unit Details" />
      ),
      cell: ({ row }) => {
        const unitDetails = row.original.input.unitDetails;
        if (!unitDetails) {
          return "-";
        }

        return `${unitDetails.unitAmount} @ ${unitDetails.normalUnitPrice}`;
      },
    },
    {
      accessorKey: "description",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Description" />
      ),
      cell: ({ row }) => row.original.input.description || "-",
    },
    {
      accessorKey: "reference",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Reference" />
      ),
      cell: ({ row }) => row.original.input.reference || "-",
    },
  ];

  const hasPreview = parsedTransactions !== null;
  const errorMessage = parseError ?? resolutionError ?? null;
  let submitLabel = "Import";
  if (importing) {
    submitLabel = "Importing...";
  }

  return (
    <div className="m-4">
      <AdminPageHeader
        title="Batch import"
        subtitle={
          fileName ||
          "Upload a CSV of transactions, review the rows, then confirm the import"
        }
        buttons={
          resolvedTransactions.length ? (
            <Button
              loading={createLoading}
              disabled={
                createLoading || createLedgerTransactionInputs.length === 0
              }
              onClick={async () => {
                try {
                  const result = await createTransactions({
                    variables: {
                      input: {
                        ledgerTransactions: createLedgerTransactionInputs,
                      },
                    },
                  });
                  const createdTransactions =
                    result.data?.createLedgerTransactions?.transactions ?? [];
                  if (createdTransactions.length === 0) {
                    throw new Error(
                      "Expected transactions in mutation response",
                    );
                  }

                  toast({
                    kind: "success",
                    autoHide: true,
                    title: "Batch import complete",
                  });

                  setParsedTransactions(null);
                  setFileName(null);
                  setParseError(null);

                  await navigate({
                    to: "/transactions",
                  });
                } catch (error) {
                  toast({
                    kind: "error",
                    autoHide: false,
                    title: "Error creating transactions",
                    description:
                      error instanceof Error ? error.message : String(error),
                  });
                }
              }}
            >
              Confirm {resolvedTransactions.length} transactions
            </Button>
          ) : undefined
        }
      />

      <div className="flex flex-row gap-8">
        <div className="flex flex-col gap-6">
          <form
            onSubmit={async (event) => {
              event.preventDefault();
              if (!selectedFile) {
                return;
              }

              await handleBatchImportFile(selectedFile);
            }}
            className="flex flex-col items-start space-y-2"
          >
            <Label htmlFor="csvFile">Upload new CSV file</Label>
            <div className="flex flex-row space-x-3">
              <div>
                <Input
                  id="csvFile"
                  type="file"
                  accept=".csv"
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    const file = event.target.files?.[0];
                    setSelectedFile(file ?? null);
                  }}
                />
              </div>

              <Button type="submit" loading={importing}>
                {submitLabel}
              </Button>
            </div>
          </form>
          <a
            href="/assets/samples/transactions-batch-import-sample.csv"
            download
            className="text-primary-interactive text-xs underline hover:text-primary-interactive"
          >
            Download sample CSV
          </a>

          {errorMessage ? <Alert title={errorMessage} kind="error" /> : null}

          {hasPreview && !errorMessage && (
            <DataTable
              data={resolvedTransactions}
              columns={columns}
              className="overflow-hidden border"
              hidePagination
              pageSize={resolvedTransactions.length}
            />
          )}
        </div>
        {!resolvedTransactions.length && (
          <Card variant="panel">
            <CardTitle>CSV format</CardTitle>
            <CardContent className="flex flex-col gap-4">
              {transactionCsvHeaders.map((header) => {
                let content: React.ReactNode;
                switch (header) {
                  case "asset":
                    content =
                      "Asset name (e.g. State Street Floating Rate Fund)";
                    break;
                  case "businessEntity":
                    content =
                      "Business Entity Name (e.g. Primary Technology Pty Limited)";
                    break;
                  case "description":
                    content = "Transaction description";
                    break;
                  case "direction":
                    content = ledgerEntryDirectionValues.join(" / ");
                    break;
                  case "effectiveAt":
                    content =
                      "Effective date of transaction dd/MM/yyyy (e.g. 25/06/2026)";
                    break;
                  case "normalAmount":
                    content = "Currency amount of the transaction";
                    break;
                  case "normalUnitPrice":
                    content =
                      "Unit price of the transaction (ignored if unitId is a currency)";
                    break;
                  case "organisation":
                    content = "Organisation Name (e.g. Primary Technology)";
                    break;
                  case "purpose":
                    content = transactionTypeValues.join(" / ");
                    break;
                  case "reference":
                    content = "Transaction reference";
                    break;
                  case "unitAmount":
                    content =
                      "Transaction amount in units (ignored if unitId is a currency)";
                    break;
                  case "unitId":
                    content =
                      'Unit of the transaction, e.g. "AUD" or "Units of State Street Floating Rate Fund"';
                    break;
                  default:
                    throw neverFunc(header);
                }

                return (
                  <Txt key={header}>
                    <strong>{header}</strong>: {content}
                  </Txt>
                );
              })}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

function parseBatchImportCsv({
  csvFileContents,
}: {
  csvFileContents: string;
}): Row[] {
  const rows = parseCsvContents(csvFileContents);
  if (rows.length === 0) {
    throw new Error("CSV is empty");
  }

  const headerRow = rows[0];
  const normalizedHeaders = headerRow.map((header) =>
    normalizeHeaderValue(header),
  );
  const missingHeaders = transactionCsvHeaders.filter(
    (header) => !normalizedHeaders.includes(header),
  );
  if (missingHeaders.length > 0) {
    throw new Error(
      `CSV is missing required columns: ${missingHeaders.join(", ")}`,
    );
  }

  const dataRows = rows
    .slice(1)
    .filter((row) => row.some((value) => value.trim().length > 0));
  if (dataRows.length === 0) {
    throw new Error("CSV has no transaction rows");
  }

  return dataRows.map((row, index) => {
    const rowNumber = index + 2;
    const rowObject = Object.fromEntries(
      normalizedHeaders.map((header, headerIndex) => [
        header,
        row[headerIndex] ?? "",
      ]),
    );
    const parsedCsvRow = transactionCsvRowSchema.safeParse(rowObject);
    if (!parsedCsvRow.success) {
      throw new Error(
        `Invalid CSV row ${rowNumber}: ${z.prettifyError(parsedCsvRow.error)}`,
      );
    }

    return {
      rowNumber,
      input: parsedCsvRow.data,
    };
  });
}

function resolveBatchTransactions({
  rows,
  assets,
}: {
  rows: Row[];
  assets: TransactionBatchAsset[];
}): ResolvedBatchTransaction[] {
  return rows.map((transaction) => {
    const asset = findMatchingAsset(transaction.input, assets);
    if (!asset) {
      throw new Error(
        `Row ${transaction.rowNumber}: Unable to match asset for row ${transaction.rowNumber}: ${transaction.input.organisation} / ${transaction.input.businessEntity} / ${transaction.input.asset}`,
      );
    }

    const businessEntity = asset.businessEntity;
    if (!businessEntity) {
      throw new Error(
        `Row ${transaction.rowNumber}: Asset "${asset.name}" is missing a business entity and cannot be imported`,
      );
    }

    const organisation = businessEntity.organisation;
    if (!organisation) {
      throw new Error(
        `Row ${transaction.rowNumber}: Business entity "${businessEntity.name}" is missing an organisation and cannot be imported`,
      );
    }

    const createLedgerTransactionInput: CreateLedgerTransactionInput = {
      assetId: asset.id,
      effectiveAt: transaction.input.effectiveAt,
      direction: transaction.input.direction,
      description: transaction.input.description,
      reference: transaction.input.reference,
      unitId: transaction.input.unitId,
      normalAmount: transaction.input.normalAmount,
      transactionType: transaction.input.purpose,
    };

    if (!isCurrency(transaction.input.unitId)) {
      const expectedUnitString = `Units of ${asset.name}`;
      if (
        transaction.input.unitId.toLowerCase() !==
        expectedUnitString.toLowerCase()
      ) {
        throw new Error(
          `Row ${transaction.rowNumber}: expected unitId to be currency or "${expectedUnitString}", got "${transaction.input.unitId}"`,
        );
      }
      const internalUnitId =
        asset.__typename === "FixedIncomeFundAsset" &&
        asset.product?.__typename === "FundProduct" &&
        asset.product.unit?.id;
      if (!internalUnitId) {
        throw new Error(
          `Row ${transaction.rowNumber}: expected to resolve unit ID for asset`,
        );
      }
      createLedgerTransactionInput.unitDetails = {
        normalUnitPrice: transaction.input.normalUnitPrice,
        unitAmount: transaction.input.unitAmount,
      };
      createLedgerTransactionInput.unitId = internalUnitId;
    }

    return {
      rowNumber: transaction.rowNumber,
      organisationName: transaction.input.organisation,
      businessEntityName: transaction.input.businessEntity,
      assetName: transaction.input.asset,
      organisationId: organisation.id,
      businessEntityId: businessEntity.id,
      assetId: asset.id,
      input: createLedgerTransactionInput,
    };
  });
}

function findMatchingAsset(
  input: Row["input"],
  assets: TransactionBatchAsset[],
): TransactionBatchAsset | undefined {
  const normalizedOrganisationName = normalizeName(input.organisation);
  const normalizedBusinessEntityName = normalizeName(input.businessEntity);
  const normalizedAssetName = normalizeName(input.asset);

  const matches = assets.filter((asset) => {
    const businessEntity = asset.businessEntity;
    if (!businessEntity || !businessEntity.organisation) {
      return false;
    }

    return (
      normalizeName(businessEntity.organisation.name) ===
        normalizedOrganisationName &&
      normalizeName(businessEntity.name) === normalizedBusinessEntityName &&
      normalizeName(asset.name) === normalizedAssetName
    );
  });

  if (matches.length === 1) {
    return matches[0];
  }

  if (matches.length > 1) {
    throw new Error(
      `Multiple assets match "${input.organisation} / ${input.businessEntity} / ${input.asset}"`,
    );
  }

  return undefined;
}

function normalizeName(value: string): string {
  return value.trim().toLowerCase();
}

function parseCsvDate(value: string): Date {
  const trimmedValue = value.trim();
  const parsedDate = DateTime.fromISO(trimmedValue, { zone: "UTC" }).isValid
    ? DateTime.fromISO(trimmedValue, { zone: "UTC" })
    : DateTime.fromFormat(trimmedValue, "dd/LL/yyyy", { zone: "UTC" });

  if (!parsedDate.isValid) {
    throw new Error(`invalid effectiveAt "${value}"`);
  }

  return parsedDate.setZone("UTC", { keepLocalTime: true }).toJSDate();
}

function parseCsvContents(csvFileContents: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let current = "";
  let inQuotes = false;
  let index = 0;

  if (csvFileContents.charCodeAt(0) === 0xfeff) {
    index = 1;
  }

  while (index < csvFileContents.length) {
    const char = csvFileContents[index];
    if (!char) {
      break;
    }

    if (char === `"`) {
      const nextChar = csvFileContents[index + 1];
      if (inQuotes && nextChar === `"`) {
        current += `"`;
        index += 2;
        continue;
      }

      inQuotes = !inQuotes;
      index += 1;
      continue;
    }

    if (!inQuotes && (char === `\n` || char === `\r`)) {
      row.push(current);
      current = "";
      if (row.some((value) => value.trim().length > 0)) {
        rows.push(row);
      }
      row = [];

      if (char === `\r` && csvFileContents[index + 1] === `\n`) {
        index += 2;
        continue;
      }

      index += 1;
      continue;
    }

    if (!inQuotes && char === `,`) {
      row.push(current);
      current = "";
      index += 1;
      continue;
    }

    current += char;
    index += 1;
  }

  row.push(current);
  if (row.some((value) => value.trim().length > 0)) {
    rows.push(row);
  }

  return rows;
}

function normalizeHeaderValue(header: string): string {
  const trimmed = header.trim().replace(/^\uFEFF/, "");
  if (trimmed.startsWith(`"`) && trimmed.endsWith(`"`)) {
    return trimmed.slice(1, -1).replace(/""/g, `"`);
  }
  return trimmed;
}
